/**
 * FmUnlockCard — compact upsell block for locked features.
 *
 * Renders a light, bordered info block with a title, optional price label,
 * teaser copy, and a CTA button that can reflect pending checkout state.
 * All colors use var(--fm-*) tokens exclusively.
 */
import React from 'react';
import { FmButton, type FmButtonVariant } from './FmButton';
import { FmInline, FmStack, FmText } from './FmComposition';
import './FmUnlockCard.css';

export interface FmUnlockCardProps {
  title: string;
  unlocksSummary?: string;
  priceLabel?: string;
  teaser?: string;
  onCtaClick: () => void;
  ctaLabel?: string;
  /**
   * Weight of the one action this card exists for. It defaults to `primary`
   * because the conversion moment may not be the quietest thing in the
   * composition — as a small grey outline beside a tinted notice it was
   * A caller with a genuinely secondary offer can
   * still step it down.
   */
  ctaVariant?: FmButtonVariant;
  /** Stable test id for the CTA button (defaults to `<data-testid>-cta`). */
  ctaTestId?: string;
  loading?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
}

export const FmUnlockCard: React.FC<FmUnlockCardProps> = ({
  title,
  unlocksSummary,
  priceLabel,
  teaser,
  onCtaClick,
  ctaLabel = 'See pricing',
  ctaVariant = 'primary',
  ctaTestId,
  loading = false,
  disabled = false,
  'data-testid': testId = 'unlock-card',
}) => (
  <div
    data-testid={testId}
    aria-busy={loading || undefined}
    className="fm-unlock-card"
  >
    <FmInline
      align="baseline"
      justify="between"
      gap="sm"
      className="fm-unlock-card__head"
    >
      <FmText as="strong" size="caption" weight="semibold">
        {title}
      </FmText>
      {priceLabel && (
        <FmText as="span" size="fine" tone="muted" className="fm-unlock-card__price">
          {priceLabel}
        </FmText>
      )}
    </FmInline>
    {/* The proposition reads darker than the caveat. It was the other way round:
        what the package GIVES was set in --fm-d-fg-3 while the disclaimer about
        what stays readable without it took --fm-d-fg-2, so the card's own offer
        was dimmer than its small print. */}
    <FmStack gap="xs">
      {unlocksSummary && (
        <FmText as="span" size="fine">
          {unlocksSummary}
        </FmText>
      )}
      {teaser && (
        <FmText as="span" size="fine" tone="subtle">
          {teaser}
        </FmText>
      )}
    </FmStack>
    <div>
      <FmButton
        variant={ctaVariant}
        disabled={disabled || loading}
        aria-busy={loading}
        onClick={onCtaClick}
        data-testid={ctaTestId ?? `${testId}-cta`}
      >
        {ctaLabel}
      </FmButton>
    </div>
  </div>
);

export default FmUnlockCard;
